import useLocalStorage from "use-local-storage";
import { useEffect, useState } from "react";
import { Box, Button, Tab, Tabs, Tooltip } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";

import {
  COMPONENT_STYLE,
  fetchGetSettingsAll,
  fetchGetTeachersAll,
  fetchPutSettingsGrades,
  Grade,
  GRADES,
  SetSnackType,
  SettingsGrades,
  SettingsNoNulls,
  TeachersNoNulls,
} from "../utils";
import { Settings } from "./admin";
import { Classrooms } from "./classrooms";
import { Conditions } from "./conditions";
import { Sorteds } from "./sorted";
import { Students } from "./students";
import { Teachers } from "./teachers";

export const HQ = (props: { disconnect: () => void; role: string; setSnack: SetSnackType }) => {
  const TABS = ["Teachers", "Classrooms", "Students", "Conditions", "Sorted"] as const;

  const [selectedTab, setSelectedTab] = useLocalStorage("selectedTab", 0);

  const [rowsLoading, setRowsLoading] = useState(false);
  const [settingsGrades, setSettingsGrades] = useState(
    GRADES.reduce((acc, el) => ({ ...acc, [el]: false }), {} as SettingsGrades),
  );
  const [targetStuNum, setTargetStuNum] = useState(0 as number | undefined);
  const [teachers, setTeachers] = useState(Array<TeachersNoNulls>());
  const [openSettings, setOpenSettings] = useState(false);

  const [powerSorting, setPowerSorting] = useState(false);

  const refresh = async () => {
    setRowsLoading(true);
    try {
      const [resultSettings, resultTeachers] = await Promise.all([fetchGetSettingsAll(), fetchGetTeachersAll()]);
      if (!resultSettings || Object.keys(resultSettings).includes("error")) {
        props.disconnect();
        props.setSnack((resultSettings as any).error?.toString(), "error");
        return;
      }
      if (!resultTeachers || Object.keys(resultTeachers).includes("error")) {
        props.disconnect();
        props.setSnack((resultTeachers as any).error?.toString(), "error");
        return;
      }

      const settings = resultSettings as SettingsNoNulls[];
      const parseSettings = (name: string) => (settings.filter(i => i.name === name)[0]?.value ?? {}) as SettingsGrades;
      setSettingsGrades(parseSettings("grades"));

      setTeachers(resultTeachers as TeachersNoNulls[]);
    } catch (err: any) {
      console.error(err);
    } finally {
      setRowsLoading(false);
    }
  };

  const updateGrade = async (grade: Grade, checked: boolean) => {
    if (settingsGrades[grade as keyof SettingsGrades] === checked) return;

    setRowsLoading(true);
    try {
      const updated = { ...settingsGrades, [grade]: checked };
      const res = await fetchPutSettingsGrades(updated);
      if (!res || Object.keys(res).includes("error")) {
        props.setSnack((res as any).error?.toString(), "error");
        throw res;
      }
      setSettingsGrades(updated);
    } catch (err: any) {
      console.error(err);
    } finally {
      setRowsLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const conflictClicked = (stuNum: number | undefined) => {
    if (!stuNum) return;
    setSelectedTab(TABS.indexOf("Conditions"));
    setTargetStuNum(stuNum);
  };

  return (
    <Box sx={COMPONENT_STYLE}>
      <Box className="no-print" display="flex" justifyContent="space-between">
        <Button
          onClick={() => {
            props.setSnack("Logged out", "success");
            props.disconnect();
          }}
          sx={{ px: 1.5 }}
        >
          Logout
        </Button>
        {props.role.includes("ADMIN") ? (
          <Button endIcon={<SettingsIcon />} onClick={() => setOpenSettings(true)} sx={{ px: 1.5 }}>
            Settings
          </Button>
        ) : (
          <Tooltip
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: "offset",
                    options: { offset: [0, -32] },
                  },
                ],
                sx: {
                  "& .MuiTooltip-tooltip": {
                    fontSize: "0.9rem",
                  },
                },
              },
            }}
            title={"Admins only"}
          >
            <span>
              <Button disabled endIcon={<SettingsIcon />} sx={{ px: 1.5 }}>
                Settings
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
      <Box className="no-print" sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs onChange={(ev, val: number) => setSelectedTab(val)} value={selectedTab}>
          {TABS.map(t => (
            <Tab
              disabled={powerSorting}
              key={`tab-${t}`}
              label={t}
              sx={{
                "&.Mui-disabled": {
                  cursor: powerSorting ? "not-allowed" : "default",
                  pointerEvents: "auto",
                },
              }}
            />
          ))}
        </Tabs>
      </Box>
      <Box>
        {(() => {
          switch (TABS[selectedTab]) {
            case "Classrooms":
              return <Classrooms {...{ ...props, settingsGrades, teachers }} />;
            case "Students":
              return <Students {...{ ...props, settingsGrades, teachers }} />;
            case "Conditions":
              return (
                <Conditions
                  {...{
                    ...props,
                    clearTarget: () => setTargetStuNum(undefined),
                    settingsGrades,
                    targetId: targetStuNum,
                    teachers,
                  }}
                />
              );
            case "Sorted":
              return <Sorteds {...{ ...props, conflictClicked, powerSorting, setPowerSorting, settingsGrades }} />;
            default:
              return <Teachers {...{ ...props, setTeachers, settingsGrades, teachers }} />;
          }
        })()}
      </Box>
      <Settings {...{ ...props, openSettings, rowsLoading, setOpenSettings, settingsGrades, updateGrade }} />
    </Box>
  );
};
